import {
  useEffect, useState,
} from 'react';
import { Search } from '@mui/icons-material';
import { Role } from 'lib/types';
import { User } from 'lib/types/users';
import { useApi } from 'lib/contexts/ApplicationState';
import { PillButton } from 'components/PillButton';
import { RoleSelect } from 'components/RoleSelect';

export interface Props {
  user: User;
  selectedRoles: Role[];
  onChange: (selected: Role[]) => void;
  multiple?: boolean;
  limit?: number;
}

export const RolePills = ({
  user, selectedRoles, onChange, multiple, limit,
}: Props) => {
  const API = useApi();
  const [roles, setRoles] = useState<Role[]>([]);
  const [unchangeable, setUnchangeable] = useState(false);
  const [isSearch, setIsSearch] = useState(false);

  const clickRole = (role: Role, selected: boolean) => {
    if (!multiple) {
      onChange([role]);
      return;
    }

    if (selected) {
      if (!limit || (limit && selectedRoles.length < limit)) {
        onChange([...selectedRoles, role]);
      }
      return;
    }
    // deselect role
    onChange(selectedRoles.filter((r) => r.id !== role.id));
  };

  const searchRole = (role: Role) => {
    // already selected, do nothing
    if (roles.findIndex((r) => r.id === role?.id) >= 0) return;

    setRoles((p) => [...p, role]);
    clickRole(role, true);
    setIsSearch(false);
  };

  useEffect(() => {
    async function fetchUserRoles() {
      if (!user?.id) {
        return;
      }

      const userRoles = await API.getUserRoles(user.id, true);
      const defaultRole = userRoles.find((r) => r.id === user.defaultRoleId);
      if (defaultRole?.unchangeableFromDefault) {
        setRoles([defaultRole]);
        setUnchangeable(true);
        return;
      }

      // Limit to 5 roles
      const ur = userRoles.sort((a) => (a.id === defaultRole?.id ? -1 : 0)).slice(0, 5);
      // selectedRoles that are not part of userRoles but need to show up
      const sr = selectedRoles.filter((r) => !ur.find((u) => u.id === r.id));
      setRoles([...sr, ...ur]);
      setUnchangeable(false);
    }

    if (user) {
      if (user.defaultRole?.unchangeableFromDefault) {
        setRoles([user.defaultRole]);
        onChange([user.defaultRole]);
        setUnchangeable(true);
        return;
      }
      fetchUserRoles();
    } else {
      setRoles([]);
    }
  }, [user?.id]);

  return (
    <div className="flex flex-wrap gap-2">
      {
        roles.map((r) => {
          const isSelected = !!selectedRoles.find((s) => s.id === r.id);
          return (
            <PillButton
              key={r.id}
              selected={isSelected}
              label={r.name}
              onClick={() => clickRole(r, !isSelected)}
            />
          );
        })
      }
      {!unchangeable && (
        <div>
          {!isSearch ? (
            <button
              type="button"
              className="border border-mediumgray font-semibold px-2 py-1 rounded-xl hover:bg-lightgray"
              onClick={() => setIsSearch(true)}
            >
              <span className="mr-2"><Search fontSize="small" /></span>
              {roles.length <= 0 ? 'Search for a Role' : 'Other'}
            </button>
          )
            : (
              <div className="w-full min-w-[240px]">
                <RoleSelect
                  selectedUser={user}
                  autoFocus
                  label="Search"
                  onChange={(r) => searchRole(r as Role)}
                  value={null}
                  excludePhasedOut
                  size="small"
                />
              </div>
            )}
        </div>
      )}
    </div>
  );
};
